import { lazy, Suspense, useEffect, useState } from 'react'
import { runtimeConfig } from './config/runtime'

const ProductionApp = lazy(() => import('./production/ProductionApp').then((module) => ({ default: module.ProductionApp })))
const QaApp = lazy(() => Promise.all([
  import('./styles/qa'),
  import('./QaApp'),
]).then(([, module]) => ({ default: module.QaApp })))

function readPathname(): string {
  return window.location.pathname
}

function usePathname(): string {
  const [pathname, setPathname] = useState(readPathname)

  useEffect(() => {
    const update = () => setPathname(readPathname())
    window.addEventListener('popstate', update)
    return () => window.removeEventListener('popstate', update)
  }, [])

  return pathname
}

function isAdminBoundary(pathname: string): boolean {
  return pathname.startsWith('/admin')
    || pathname.startsWith('/__qa/admin')
    || pathname.startsWith('/__health/admin')
}

function RouteLoading() {
  return <main id="main-content" className="route-loading" aria-live="polite" aria-busy="true"><p>Loading Obelanse…</p></main>
}

function ProductionConfigurationRequired({ admin, issues }: { admin: boolean; issues: readonly string[] }) {
  return (
    <main id="main-content" className="main-content" tabIndex={-1}>
      <section className="content-card" aria-labelledby="production-configuration-title">
        <p className="eyebrow">Production setup</p>
        <h1 id="production-configuration-title">{admin ? 'Admin access is temporarily unavailable' : 'Ordering is temporarily unavailable'}</h1>
        <p>{admin
          ? 'The protected Admin workspace is not allowed to start with incomplete API or authentication configuration.'
          : 'The production website is not allowed to start with incomplete API or content configuration.'}</p>
        <ul className="check-list">
          {issues.map((issue) => <li key={issue}>{issue}</li>)}
        </ul>
      </section>
    </main>
  )
}

function App() {
  const pathname = usePathname()
  const admin = isAdminBoundary(pathname)
  const productionIssues = admin
    ? runtimeConfig.adminProductionIssues
    : runtimeConfig.publicProductionIssues

  if (runtimeConfig.isProduction && productionIssues.length > 0) {
    return <ProductionConfigurationRequired admin={admin} issues={productionIssues} />
  }

  return (
    <Suspense fallback={<RouteLoading />}>
      {runtimeConfig.isProduction ? <ProductionApp /> : <QaApp />}
    </Suspense>
  )
}

export default App
